/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Subscribe to paragraph content changes (Word JavaScript API 1.6).
 * Invokes `onChanged` when the user edits paragraph text; used to drive
 * {@link import('./auto-save-loop.js').AutoSaveLoop.notifyChanged}.
 *
 * If registration fails (unsupported host), returns a no-op unsubscribe.
 *
 * @remarks
 * Api set: WordApi 1.6 ({@link https://learn.microsoft.com/javascript/api/word/word.document#word-word-document-onparagraphchanged-member})
 */
export async function subscribeParagraphChanges(onChanged: () => void): Promise<() => void> {
  try {
    let registration:
      | OfficeExtension.EventHandlerResult<Word.ParagraphChangedEventArgs>
      | undefined;

    await Word.run(async (context) => {
      const handler = async (_args: Word.ParagraphChangedEventArgs): Promise<void> => {
        onChanged();
      };
      registration = context.document.onParagraphChanged.add(handler);
      await context.sync();
    });

    return () => {
      void Word.run(async (context) => {
        registration?.remove();
        await context.sync();
      });
    };
  } catch {
    return () => undefined;
  }
}
